import Image from "next/image"
import Link from "next/link"
import { useTranslations } from "next-intl"
import { ArrowRight, Heart, Search } from "lucide-react"
import { CUISINE_LABELS } from "@/lib/marketplace/constants"
import { formatClock12h, priceTierLabel } from "@/lib/marketplace/cardSignals"
import { distanceBand } from "@/lib/geo/haversine"
import type { MarketplaceMerchant } from "@/lib/marketplace/types"
import { brandAvatarUrl } from "@/lib/branding/brandIcon"

type Props = {
  merchant: MarketplaceMerchant
  isFavorite?: boolean
  onToggleFavorite?: (id: string) => void
  /** Menu dish that matched the active search — shown as a "has this" hint. */
  matchedDish?: string
  /**
   * F3: distancia en millas calculada EN EL NAVEGADOR (la posición del
   * visitante nunca sube al servidor). Solo con dato se pinta la banda —
   * y siempre banda ("~2 mi"), nunca decimales: el origen puede ser un
   * centroide de ZIP y "3.2 mi" sería precisión inventada.
   */
  distanceMi?: number | null
  /**
   * F1b: prefijo del enlace al storefront. "/order" en useredy.com; "" en el
   * dominio consumer, donde la forma canónica es la corta (redytoeat.com/{slug})
   * y la larga redirige 308 — sin esto, CADA tap del comensal pagaría un salto
   * extra en la ruta caliente. Lo decide el servidor por Host; el componente
   * solo lo concatena.
   */
  linkBase?: string
}

export function MerchantCard({
  merchant,
  isFavorite,
  onToggleFavorite,
  matchedDish,
  distanceMi,
  linkBase = "/order",
}: Props) {
  const t = useTranslations("marketplace")
  const displayTagline = merchant.marketplace_tagline ?? merchant.tagline
  // El guard `in CUISINE_LABELS` valida contra el enum; el label visible sale
  // del namespace i18n (mismas 12 claves — guard de paridad en __tests__).
  const cuisineLabel =
    merchant.marketplace_cuisine && merchant.marketplace_cuisine in CUISINE_LABELS
      ? t(`cuisines.${merchant.marketplace_cuisine}`)
      : null
  const meta = [cuisineLabel, priceTierLabel(merchant.price_tier), merchant.city]
    .filter(Boolean)
    .join(" · ")
  const distanceLabel = typeof distanceMi === "number" ? distanceBand(distanceMi) : null
  // Cover cascade: marketplace cover → best/featured dish photo → logo → initial.
  const coverSrc = merchant.cover_image_url ?? merchant.fallback_dish_image
  const hasImage = !!coverSrc
  // Avatar cuadrado con `cover`: el ícono de la marca si lo hay (un wordmark
  // apaisado recortado ahí queda ilegible); si no, el logo como antes.
  const avatarSrc = brandAvatarUrl(merchant)

  return (
    <Link
      href={`${linkBase}/${merchant.slug}`}
      className="group block bg-white rounded-2xl border border-[#E8E4DF] overflow-hidden transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(0,0,0,0.08)]"
    >
      {/* Cover */}
      <div className="relative h-44 bg-[#F4F0EA] overflow-hidden">
        {hasImage ? (
          <Image
            src={coverSrc!}
            alt={merchant.name}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
            className="object-cover transition-transform duration-300 group-hover:scale-[1.04]"
          />
        ) : (
          // Rich fallback: the real logo centered (or initial), not a faint letter
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ background: "linear-gradient(135deg,#FBF9F6,#EFE8DF)" }}
          >
            {avatarSrc ? (
              <div className="relative w-16 h-16 rounded-2xl bg-white border border-[#E8E4DF] overflow-hidden shadow-sm">
                <Image src={avatarSrc} alt="" fill className="object-cover" />
              </div>
            ) : (
              <span className="text-5xl font-black text-[#D7263D]/25 select-none">
                {merchant.name[0]?.toUpperCase()}
              </span>
            )}
          </div>
        )}

        {/* Top scrim so the badge reads on any photo */}
        {hasImage && (
          <div className="absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-black/35 to-transparent pointer-events-none" />
        )}

        {/* Open/Closed pill — top-left (no longer collides with the logo) */}
        <div
          className="absolute top-2.5 left-2.5 flex items-center gap-1.5 rounded-full px-2.5 py-1"
          style={{ background: "rgba(0,0,0,0.55)" }}
        >
          <span
            className={`w-[6px] h-[6px] rounded-full ${merchant.isOpen ? "bg-[#22c55e]" : "bg-[#9CA3AF]"}`}
          />
          <span className="text-white text-[11px] font-semibold">
            {merchant.isOpen
              ? merchant.closesAt
                ? t("card.openUntil", { time: formatClock12h(merchant.closesAt) })
                : t("card.open")
              : t("card.closed")}
          </span>
        </div>

        {/* Small logo overlay only when there IS a cover (otherwise the logo is the centerpiece) */}
        {hasImage && avatarSrc && (
          <div className="absolute bottom-[-14px] left-3 w-11 h-11 rounded-xl bg-white border border-[#E8E4DF] overflow-hidden shadow-sm">
            <Image src={avatarSrc} alt="" fill className="object-cover" />
          </div>
        )}

        {/* Favorite heart — top-right; stops the card's <Link> from navigating */}
        {onToggleFavorite && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onToggleFavorite(merchant.id)
            }}
            aria-label={isFavorite ? t("card.removeFavorite") : t("card.saveFavorite")}
            aria-pressed={!!isFavorite}
            className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-sm transition-transform hover:scale-105"
            style={{ background: "rgba(255,255,255,0.92)" }}
          >
            <Heart
              className="w-[17px] h-[17px]"
              color={isFavorite ? "#D7263D" : "#7A7A7A"}
              fill={isFavorite ? "#D7263D" : "none"}
              strokeWidth={2}
            />
          </button>
        )}
      </div>

      {/* Body */}
      <div className={`px-3.5 pb-3.5 ${hasImage && merchant.logo_url ? "pt-6" : "pt-3"}`}>
        <div className="font-bold text-[15px] text-[#1A1A1A] leading-tight truncate">
          {merchant.name}
        </div>
        {/* Rating solo con reseñas PUBLICADAS reales (v_location_review_stats).
            Sin reseñas no se pinta nada: "0.0 (0)" sería un dato inventado. */}
        {merchant.reviewCount >= 1 && (
          <div
            className="mt-1 flex items-center gap-1 text-[12.5px]"
            aria-label={t("card.ratingAria", {
              rating: (merchant.avgRating ?? 0).toFixed(1),
              count: merchant.reviewCount,
            })}
          >
            <svg
              viewBox="0 0 20 20"
              className="marketplace-rating-star w-[13px] h-[13px] flex-shrink-0"
              fill="#F5A623"
              aria-hidden="true"
            >
              <path d="M10 1.5l2.63 5.32 5.87.86-4.25 4.14 1 5.85L10 14.9l-5.25 2.77 1-5.85L1.5 7.68l5.87-.86L10 1.5z" />
            </svg>
            <span className="font-semibold text-[#1A1A1A]">
              {(merchant.avgRating ?? 0).toFixed(1)}
            </span>
            <span className="text-[#7A7A7A]">({merchant.reviewCount})</span>
          </div>
        )}
        {matchedDish && (
          <div className="mt-1.5 inline-flex max-w-full items-center gap-1 rounded-full bg-[#fff5f6] px-2 py-0.5 text-[11px] font-semibold text-[#D7263D]">
            <Search className="w-3 h-3 flex-shrink-0" strokeWidth={2.5} aria-hidden />
            <span className="truncate">{matchedDish}</span>
          </div>
        )}
        {(distanceLabel || meta) && (
          <div className="text-[12.5px] text-[#7A7A7A] mt-0.5 truncate">
            {distanceLabel && (
              <span
                className="font-semibold text-[#1A1A1A]"
                aria-label={t("card.distanceAria", { band: distanceLabel })}
              >
                {distanceLabel}
              </span>
            )}
            {distanceLabel && meta ? " · " : null}
            {meta}
          </div>
        )}
        {displayTagline && (
          <div className="text-[12px] text-[#9A968F] mt-1 line-clamp-1">{displayTagline}</div>
        )}
        <div
          className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-xl py-2 text-[13px] font-semibold text-white transition-opacity group-hover:opacity-90"
          style={{ background: "linear-gradient(90deg,#D7263D,#F46B35)" }}
        >
          {t("card.order")} <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </div>
      </div>
    </Link>
  )
}
